import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Check, Star, ChevronLeft, ChevronRight } from 'lucide-react';

export const PricingSection = ({ openPopup }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef(null);

  const plans = [
    {
      name: "Essentials",
      price: "$45",
      tagline: "Perfect for startups",
      features: [
        "Case Review",
        "Case Filing",
        "Basic Search",
        "State E-Filing",
        "Trademark Secured"
      ],
      note: "Govt. filing fee not included",
      popular: false
    },
    {
      name: "Enhanced",
      price: "$149",
      tagline: "Most chosen by small businesses",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "High Satisfaction Rate",
        "Refusal Risk Meter"
      ],
      note: "Govt. filing fee not included",
      popular: false
    },
    {
      name: "Deluxe",
      price: "$299",
      tagline: "Recommended",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "High Satisfaction Rate",
        "Refusal Risk Meter",
        "Trademark Monitoring",
        "Complete Documentation",
        "Digital File",
        "Dedicated Case Manager"
      ],
      note: "Govt. filing fee not included",
      popular: true
    },
    {
      name: "Platinum",
      price: "$349",
      tagline: "Ultimate protection",
      features: [
        "Case Review",
        "Case Filing",
        "Direct Hit Search",
        "Trademark Alert",
        "Trademark Secured",
        "High Satisfaction Rate",
        "Refusal Risk Meter",
        "Trademark Monitoring",
        "Complete Documentation",
        "Digital File",
        "Dedicated Case Manager",
        "High Approval Rate",
        "Comprehensive Search Report",
        "High Priority Service",
        "Cease & Desist Letter"
      ],
      note: "Govt. filing fee not included",
      popular: false
    }
  ];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % plans.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + plans.length) % plans.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section id="pricing" className="py-20 bg-[#FFF7ED]">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#7C2D12] mb-4">
              Transparent Pricing Plans
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the trademark protection package that fits your needs and budget. All plans include expert guidance and USPTO filing.
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <Card 
                key={index}
                className={`relative p-8 bg-white border-2 transition-all duration-300 hover:shadow-2xl flex flex-col ${
                  plan.popular 
                    ? 'border-[#F97316] shadow-xl scale-105' 
                    : 'border-[#FED7AA] hover:border-[#F97316]'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-[#F97316] text-white px-4 py-1 rounded-full text-sm font-bold flex items-center">
                      <Star size={14} className="mr-1" fill="white" />
                      {plan.tagline}
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-[#7C2D12] mb-2">
                    {plan.name}
                  </h3>
                  {!plan.popular && (
                    <p className="text-sm text-gray-500 mb-3">{plan.tagline}</p>
                  )}
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-[#F97316]">
                      {plan.price}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">+ Federal Filing Fee</p>
                </div>

                <div className="flex-grow mb-6 overflow-y-auto max-h-80 scrollbar-thin scrollbar-thumb-[#F97316] scrollbar-track-gray-200">
                  <ul className="space-y-3">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start">
                        <Check className="text-[#F97316] mr-2 flex-shrink-0 mt-1" size={18} />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-auto">
                  <p className="text-xs text-gray-500 text-center mb-4 italic">
                    {plan.note}
                  </p>
                  <Button 
                    onClick={openPopup}
                    className={`w-full py-6 font-semibold transition-all duration-300 ${
                      plan.popular
                        ? 'bg-[#F97316] hover:bg-[#7C2D12] text-white'
                        : 'bg-[#7C2D12] hover:bg-[#F97316] text-white'
                    }`}
                  >
                    Get Started
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {/* Mobile Slider */}
          <div className="md:hidden relative">
            <div className="overflow-hidden" ref={scrollContainerRef}>
              <div 
                className="flex transition-transform duration-300 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {plans.map((plan, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-4">
                    <Card 
                      className={`p-8 bg-white border-2 ${
                        plan.popular 
                          ? 'border-[#F97316] shadow-xl' 
                          : 'border-[#FED7AA]'
                      }`}
                    >
                      {plan.popular && (
                        <div className="text-center mb-4">
                          <div className="inline-block bg-[#F97316] text-white px-4 py-1 rounded-full text-sm font-bold">
                            <Star size={14} className="inline mr-1" fill="white" />
                            {plan.tagline}
                          </div>
                        </div>
                      )}

                      <div className="text-center mb-6">
                        <h3 className="text-2xl font-bold text-[#7C2D12] mb-2">
                          {plan.name}
                        </h3>
                        {!plan.popular && (
                          <p className="text-sm text-gray-500 mb-3">{plan.tagline}</p>
                        )}
                        <div className="mb-2">
                          <span className="text-4xl font-bold text-[#F97316]">
                            {plan.price}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">+ Federal Filing Fee</p>
                      </div>

                      <div className="mb-6 max-h-64 overflow-y-auto">
                        <ul className="space-y-3">
                          {plan.features.map((feature, fIndex) => (
                            <li key={fIndex} className="flex items-start">
                              <Check className="text-[#F97316] mr-2 flex-shrink-0 mt-1" size={18} />
                              <span className="text-gray-700 text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <p className="text-xs text-gray-500 text-center mb-4 italic">
                        {plan.note}
                      </p>
                      <Button 
                        onClick={openPopup}
                        className="w-full py-6 bg-[#F97316] hover:bg-[#7C2D12] text-white font-semibold transition-all duration-300"
                      >
                        Get Started
                      </Button>
                    </Card>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
              aria-label="Previous plan"
            >
              <ChevronLeft className="text-[#7C2D12]" size={24} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
              aria-label="Next plan"
            >
              <ChevronRight className="text-[#7C2D12]" size={24} />
            </button>

            {/* Dots Navigation */}
            <div className="flex justify-center mt-6 space-x-2">
              {plans.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentSlide === index 
                      ? 'bg-[#F97316] w-8' 
                      : 'bg-gray-300'
                  }`}
                  aria-label={`Go to plan ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Bottom Note */}
          <div className="mt-12 text-center">
            <Card className="p-6 bg-white border-2 border-[#FED7AA] max-w-3xl mx-auto">
              <p className="text-gray-700">
                <strong className="text-[#7C2D12]">Note:</strong> All plans require the USPTO government filing fee of $250-$350 per class, payable directly to the USPTO. We'll guide you through the process.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
